import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { useNavigate, useParams, Link } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import useThemeColor from "../../hooks/useThemeColor";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ProductWithRecipeSelectionModal from "../../components/CustomComponents/modal/ProductWithRecipeSelectionModal";

const CreateUpdateBatch = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const isEditMode = !!batchId;
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [loadingIngredients, setLoadingIngredients] = useState(false);
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [productMeasurementType, setProductMeasurementType] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeFormData, setCompleteFormData] = useState({
    finalQuantity: "",
    finalUnit: "",
    expiryDate: "",
    batchNumber: "",
  });

  // Form state
  const [formData, setFormData] = useState({
    batchId: 0,
    batchNumber: "",
    menuItemId: 0,
    quantity: "",
    unit: "",
    expiryDate: "",
    costPrice: "",
    totalProductionCost: 0,
    costPerUnit: 0,
    batchStatus: "Draft",
    isCompleted: false,
    completedDate: "",
    wasteQuantity: "",
    wasteReason: "",
    compID: CompID,
    notes: "",
  });

  // Fetch units
  const fetchUnits = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/Unit/SuperAdmin/GetAllUnits", {
        params: {
          SortBy: "MeasureName",
          SortOrder: "ASC",
          Search: "",
          PageSize: "1000",
          PageNumber: 1,
        },
      });

      if (response.data.status) {
        const unitsData = response.data.data || [];
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  }, []);

  // Fetch product details to get measurement type
  const fetchProductDetails = useCallback(async (menuItemId) => {
    if (!menuItemId || !CompID) return;

    try {
      const response = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
        params: { CompID, MenuItem_ID: menuItemId },
      });

      const itemData = response.data?.data || response.data;
      if (itemData) {
        const measurementType = itemData.menuItem_MeasurementType || itemData.MenuItem_MeasurementType || "Unit";
        setProductMeasurementType(measurementType);
        return measurementType;
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
    return null;
  }, [CompID]);

  // Filter units based on measurement type
  useEffect(() => {
    if (productMeasurementType && units.length > 0) {
      const filtered = units.filter(
        (unit) => (unit.MeasurementTypeName || unit.measurementTypeName) === productMeasurementType
      );
      setFilteredUnits(filtered);
    } else {
      setFilteredUnits([]);
    }
  }, [productMeasurementType, units]);

  // Fetch products with recipes only
  const fetchMenuItems = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      // Fetch products that have recipes
      const response = await axiosInstance.get(APIs.GET_ALL_RECIPES_BY_COMPANY, {
        params: { CompID },
      });
      
      const recipesData = response.data?.data || response.data || [];
      const validRecipes = Array.isArray(recipesData) ? recipesData : [];
      
      // Map to dropdown options
      const items = validRecipes.map((item) => ({
        value: item.menuItem_ID || item.menuItemId,
        label: item.menuItem_Name || item.menuItemName || `Item #${item.menuItem_ID || item.menuItemId}`,
        data: item, // Store full item data for later use
      }));
      
      setMenuItems(items);
      return items; // Return items for use in useEffect
    } catch (error) {
      console.error("Error fetching products with recipes:", error);
      showErrorToast("Failed to load products with recipes");
      setMenuItems([]);
      return [];
    } finally {
      if (!isEditMode) {
        setLoading(false);
      }
    }
  }, [CompID, isEditMode]);

  // Fetch recipe ingredients when product is selected (only for draft/in-progress batches)
  const fetchRecipeIngredients = useCallback(async (menuItemId) => {
    if (!menuItemId || !CompID) {
      setIngredients([]);
      return;
    }

    // Don't fetch recipe ingredients if batch is completed - use batch ingredient costs instead
    const isBatchCompleted = formData.isCompleted === true || formData.isCompleted === 1 || formData.batchStatus === "Completed";
    if (isBatchCompleted) return;

    try {
      setLoadingIngredients(true);
 
      const response = await axiosInstance.get(APIs.GET_RECIPE_INGREDIENTS, {
        params: { MenuItem_ID: menuItemId, CompID },
      });

      const ingredientsData = response.data?.data || response.data || [];
      const validIngredients = Array.isArray(ingredientsData) ? ingredientsData : [];
  
        // Format ingredients for display
        const formattedIngredients = validIngredients.map((ing) => ({
          id: ing.id || ing.ID || Date.now() + Math.random(),
          ingredientID: ing.ingredientID || ing.IngredientID,
          ingredientName: ing.ingredientName || ing.IngredientName || "Unknown",
          quantity: ing.quantity || ing.Quantity || 0,
          unit: ing.selectedUnit || ing.SelectedUnit || ing.unit || "Units",
          costPrice: ing.unitCost || ing.UnitCost || 0,
          cost: ing.cost || ing.Cost || 0,
        }));
        setIngredients(formattedIngredients);
    } catch (error) {
      console.error("Error fetching recipe ingredients:", error);
      setIngredients([]);
      // Don't show error toast as it's expected that some products might not have ingredients yet
    } finally {
      setLoadingIngredients(false);
    }
  }, [CompID, formData.isCompleted, formData.batchStatus]);

  // Calculate recipe cost from ingredients
  const calculateRecipeCost = useCallback(async (ingredientsData, menuItemId) => {
    if (!ingredientsData || ingredientsData.length === 0) return 0;

    try {
      // Get units for cost calculation
      const response = await axiosInstance.get("/api/Unit/SuperAdmin/GetAllUnits", {
        params: {
          SortBy: "MeasureName",
          SortOrder: "ASC",
          Search: "",
          PageSize: "1000",
          PageNumber: 1,
        },
      });

      const units = response.data.status ? (response.data.data || []) : [];

      let totalCost = 0;

      for (const ingredient of ingredientsData) {
        const cost = parseFloat(ingredient.cost) || 0;
        if (cost > 0) {
          totalCost += cost;
        }
      }

      return totalCost;
    } catch (error) {
      console.error("Error calculating recipe cost:", error);
      return 0;
    }
  }, []);

  // Handle product selection change
  const handleProductChange = async (selected) => {
     const menuItemId = selected?.value || 0;
    setFormData(prev => ({ ...prev, menuItemId, costPrice: "" })); // Reset cost price when changing product
    setSelectedProduct(selected?.data || null);

    if (menuItemId) {
      await fetchProductDetails(menuItemId);
   
      // Only fetch recipe ingredients if batch is not completed
      // If batch is completed, ingredient costs will be loaded from batch data
      const isBatchCompleted = formData.isCompleted === true || formData.isCompleted === 1 || formData.batchStatus === "Completed";
      
      if (!isBatchCompleted) {
        // Fetch recipe ingredients for draft/in-progress batches
        try {
          setLoadingIngredients(true);
          const response = await axiosInstance.get(APIs.GET_RECIPE_INGREDIENTS, {
            params: { MenuItem_ID: menuItemId, CompID },
          });

          const ingredientsData = response.data?.data || response.data || [];
          const validIngredients = Array.isArray(ingredientsData) ? ingredientsData : [];

          const formattedIngredients = validIngredients.map((ing) => ({
            id: ing.id || ing.ID || Date.now() + Math.random(),
            ingredientID: ing.ingredientID || ing.IngredientID,
            ingredientName: ing.ingredientName || ing.IngredientName || "Unknown",
            quantity: ing.quantity || ing.Quantity || 0,
            unit: ing.selectedUnit || ing.SelectedUnit || ing.unit || "Units",
            costPrice: ing.unitCost || ing.UnitCost || 0,
            cost: ing.cost || ing.Cost || 0,
          }));
          setIngredients(formattedIngredients);

          // Calculate total recipe cost
          const recipeCost = await calculateRecipeCost(validIngredients, menuItemId);

          // Set the calculated recipe cost as the batch cost price
          setFormData(prev => ({
            ...prev,
            costPrice: recipeCost > 0 ? recipeCost.toFixed(2) : ""
          }));

        } catch (error) {
          console.error("Error fetching recipe ingredients:", error);
          setIngredients([]);
          showErrorToast("Failed to load recipe cost");
        } finally {
          setLoadingIngredients(false);
        }
      } else {
        // For completed batches, keep existing batch ingredient costs (don't overwrite with recipe)
      }
    } else {
      setIngredients([]);
      setProductMeasurementType(null);
      setFilteredUnits([]);
    }
  };


  // Handle Start Batch button
  const handleStartBatch = async () => {
    if (!formData.batchId) {
      showErrorToast("Please save the batch first");
      return;
    }

    try {
      setFormLoading(true);
      const payload = {
        batchId: formData.batchId,
        batchNumber: formData.batchNumber.trim(),
        menuItemId: formData.menuItemId,
        quantity: parseFloat(formData.quantity) || 0,
        unit: formData.unit.trim(),
        expiryDate: formData.expiryDate || null,
        costPrice: parseFloat(formData.costPrice) || 0,
        totalProductionCost: parseFloat(formData.totalProductionCost) || 0,
        costPerUnit: parseFloat(formData.costPerUnit) || 0,
        batchStatus: "InProgress",
        isCompleted: false,
        completedDate: null,
        wasteQuantity: formData.wasteQuantity ? parseFloat(formData.wasteQuantity) : null,
        wasteReason: formData.wasteReason || null,
        compID: CompID,
        notes: formData.notes || null,
      };

      const response = await axiosInstance.put(APIs.PUT_UPDATE_RECIPE_BATCH, payload);

      if (response.data && response.data.status) {
        showSuccessToast("Batch started successfully");
        setFormData({ ...formData, batchStatus: "InProgress" });
      } else {
        showErrorToast(response.data?.message || "Failed to start batch");
      }
    } catch (error) {
      console.error("Error starting batch:", error);
      showErrorToast(error.response?.data?.message || "Failed to start batch");
    } finally {
      setFormLoading(false);
    }
  };

  // Handle Complete Batch button
  const handleOpenCompleteModal = () => {
    setCompleteFormData({
      finalQuantity: formData.quantity || "",
      finalUnit: formData.unit || "",
      expiryDate: formData.expiryDate || "",
      batchNumber: formData.batchNumber || "",
    });
    setShowCompleteModal(true);
  };

  const handleCompleteBatch = async () => {
    if (!completeFormData.batchNumber.trim()) {
      showErrorToast("Batch Number is required");
      return;
    }
    if (!completeFormData.finalQuantity || parseFloat(completeFormData.finalQuantity) <= 0) {
      showErrorToast("Final Quantity is required and must be greater than 0");
      return;
    }
    if (!completeFormData.finalUnit.trim()) {
      showErrorToast("Final Unit is required");
      return;
    }

    try {
      setFormLoading(true);
      const now = new Date();
      const payload = {
        batchId: formData.batchId,
        batchNumber: completeFormData.batchNumber.trim(),
        menuItemId: formData.menuItemId,
        quantity: parseFloat(completeFormData.finalQuantity),
        unit: completeFormData.finalUnit.trim(),
        expiryDate: completeFormData.expiryDate || null,
        costPrice: parseFloat(formData.costPrice) || 0,
        totalProductionCost: parseFloat(formData.totalProductionCost) || 0,
        costPerUnit: parseFloat(formData.costPerUnit) || 0,
        batchStatus: "Completed",
        isCompleted: true,
        completedDate: now.toISOString().split('T')[0],
        wasteQuantity: formData.wasteQuantity ? parseFloat(formData.wasteQuantity) : null,
        wasteReason: formData.wasteReason || null,
        compID: CompID,
        notes: formData.notes || null,
      };

      const response = await axiosInstance.put(APIs.PUT_UPDATE_RECIPE_BATCH, payload);

      if (response.data && response.data.status) {
        // Stock deduction and addition are now handled automatically in the service layer
        // when the batch status is set to "Completed"

        showSuccessToast("Batch completed successfully");
        
        // Refresh batch data to get updated ingredient costs
        const batchResponse = await axiosInstance.get(APIs.GET_RECIPE_BATCH_BY_ID, {
          params: {
            BatchId: formData.batchId,
            CompID: CompID,
          },
        });
        
        if (batchResponse.data && batchResponse.data.status && batchResponse.data.data) {
          const updatedBatch = batchResponse.data.data;
          
          setFormData({
            ...formData,
            batchNumber: completeFormData.batchNumber,
            batchStatus: "Completed",
            isCompleted: true,
            completedDate: now.toISOString().split('T')[0],
            quantity: completeFormData.finalQuantity,
            unit: completeFormData.finalUnit,
            totalProductionCost: updatedBatch.totalProductionCost || 0,
            costPerUnit: updatedBatch.costPerUnit || 0,
          });
          
          // Update ingredients with stored costs from batch completion
          if (updatedBatch.ingredientCosts && Array.isArray(updatedBatch.ingredientCosts) && updatedBatch.ingredientCosts.length > 0) {
            const formattedIngredients = updatedBatch.ingredientCosts.map((ic) => ({
              id: ic.id || ic.ID || Date.now() + Math.random(),
              ingredientID: ic.ingredientID || ic.IngredientID,
              ingredientName: ic.ingredientName || ic.IngredientName || "Unknown",
              quantity: ic.quantityUsed || ic.QuantityUsed || 0,
              unit: ic.unit || ic.Unit || "Units",
              costPrice: ic.weightedAverageCostPerUnit || ic.WeightedAverageCostPerUnit || 0,
              cost: ic.totalCost || ic.TotalCost || 0,
            }));
            setIngredients(formattedIngredients);
          }
        } else {
          // Fallback if refresh fails
          setFormData({
            ...formData,
            batchNumber: completeFormData.batchNumber,
            batchStatus: "Completed",
            isCompleted: true,
            completedDate: now.toISOString().split('T')[0],
            quantity: completeFormData.finalQuantity,
            unit: completeFormData.finalUnit,
          });
        }
        
        setShowCompleteModal(false);
      } else {
        // Show error and stay on the same page
        const errorMessage = response.data?.message || "Failed to complete batch";
        showErrorToast(errorMessage);
        console.error("Batch completion failed:", response.data);
      }
    } catch (error) {
      console.error("Error completing batch:", error);
      
      // Extract error message from response - handle validation errors
      let errorMessage = "Failed to complete batch. Please check your input and try again.";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle validation errors (400 Bad Request)
        if (errorData.errors) {
          const validationErrors = [];
          Object.keys(errorData.errors).forEach(field => {
            const fieldErrors = errorData.errors[field];
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach(err => validationErrors.push(`${field}: ${err}`));
            } else {
              validationErrors.push(`${field}: ${fieldErrors}`);
            }
          });
          errorMessage = validationErrors.join('\n');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.title) {
          errorMessage = errorData.title;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showErrorToast(errorMessage);
      // Stay on the same page - don't close modal on error
    } finally {
      setFormLoading(false);
    }
  };

  // Fetch batch data for edit mode
  const fetchBatchData = useCallback(async (menuItemsList) => {
    if (!isEditMode || !batchId || !CompID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_RECIPE_BATCH_BY_ID, {
        params: {
          BatchId: parseInt(batchId),
          CompID: CompID,
        },
      });

      if (response.data && response.data.status && response.data.data) {
        const batch = response.data.data;
        const menuItemId = batch.menuItemId || 0;
        
        setFormData({
          batchId: batch.batchId || 0,
          batchNumber: batch.batchNumber || "",
          menuItemId: menuItemId,
          quantity: batch.quantity || "",
          unit: batch.unit || "", // Unit can be null/empty for draft batches
          expiryDate: batch.expiryDate ? new Date(batch.expiryDate).toISOString().split('T')[0] : "",
          costPrice: batch.costPrice || "",
          totalProductionCost: batch.totalProductionCost || 0,
          costPerUnit: batch.costPerUnit || 0,
          batchStatus: batch.batchStatus || "Draft",
          isCompleted: batch.isCompleted || false,
          completedDate: batch.completedDate ? new Date(batch.completedDate).toISOString().split('T')[0] : "",
          wasteQuantity: batch.wasteQuantity || "",
          wasteReason: batch.wasteReason || "",
          compID: CompID,
          notes: batch.notes || "",
        });

        // Fetch product details and ingredients for the selected product
        if (menuItemId) {
          await fetchProductDetails(menuItemId);
          
          // If batch is completed, use stored ingredient costs; otherwise fetch recipe ingredients
          const isBatchCompleted = batch.isCompleted === true || batch.isCompleted === 1 || 
                                   batch.isCompleted === "true" || batch.isCompleted === "1" ||
                                   batch.batchStatus === "Completed" || batch.batchStatus === "completed";
          
          if (isBatchCompleted && batch.ingredientCosts && Array.isArray(batch.ingredientCosts) && batch.ingredientCosts.length > 0) {
            // Use stored ingredient costs from batch completion (actual costs used)
            const formattedIngredients = batch.ingredientCosts.map((ic) => ({
              id: ic.id || ic.ID || Date.now() + Math.random(),
              ingredientID: ic.ingredientID || ic.IngredientID,
              ingredientName: ic.ingredientName || ic.IngredientName || "Unknown",
              quantity: ic.quantityUsed || ic.QuantityUsed || 0,
              unit: ic.unit || ic.Unit || "Units",
              costPrice: ic.weightedAverageCostPerUnit || ic.WeightedAverageCostPerUnit || 0,
              cost: ic.totalCost || ic.TotalCost || 0,
            }));
            setIngredients(formattedIngredients);
          } else if (isBatchCompleted) {
            setIngredients([]);
          } else {
            await fetchRecipeIngredients(menuItemId);
          }
          
          // Find and set selected product
          const product = menuItemsList.find(item => item.value === menuItemId);
          if (product) {
            setSelectedProduct(product.data);
          }
        }
      } else {
        showErrorToast("Batch not found");
        navigate("/batches/manageBatch");
      }
    } catch (error) {
      console.error("Error fetching batch:", error);
      showErrorToast("Failed to load batch data");
      navigate("/batches/manageBatch");
    } finally {
      setLoading(false);
    }
  }, [isEditMode, batchId, CompID, navigate, fetchRecipeIngredients, fetchProductDetails]);

  useEffect(() => {
    if (CompID) {
      fetchUnits();
      fetchMenuItems().then((items) => {
        if (isEditMode && items && items.length > 0) {
          fetchBatchData(items);
        } else if (!isEditMode) {
          setLoading(false);
        }
      });
    }
  }, [CompID, fetchMenuItems, isEditMode, fetchBatchData, fetchUnits]);

  const handleSubmit = async () => {
    if (!formData.batchNumber.trim()) {
      showErrorToast("Batch Number is required");
      return;
    }
    if (!formData.menuItemId || formData.menuItemId === 0) {
      showErrorToast("Please select a product");
      return;
    }
    if (!formData.costPrice || parseFloat(formData.costPrice) < 0) {
      showErrorToast("Cost Price is required");
      return;
    }

    try {
      setFormLoading(true);
      const payload = {
        batchId: isEditMode ? formData.batchId : 0,
        batchNumber: formData.batchNumber.trim(),
        menuItemId: formData.menuItemId,
        quantity: formData.quantity ? parseFloat(formData.quantity) : 0,
        // Unit is nullable - only required when completing the batch
        unit: formData.unit && formData.unit.trim() ? formData.unit.trim() : null,
        expiryDate: formData.expiryDate || null,
        costPrice: parseFloat(formData.costPrice),
        totalProductionCost: parseFloat(formData.totalProductionCost) || 0,
        costPerUnit: parseFloat(formData.costPerUnit) || 0,
        batchStatus: formData.batchStatus,
        isCompleted: formData.isCompleted,
        completedDate: formData.completedDate || null,
        wasteQuantity: formData.wasteQuantity ? parseFloat(formData.wasteQuantity) : null,
        wasteReason: formData.wasteReason || null,
        compID: CompID,
        notes: formData.notes || null,
      };

      const endpoint = isEditMode 
        ? APIs.PUT_UPDATE_RECIPE_BATCH 
        : APIs.POST_ADD_RECIPE_BATCH;
      
      const method = isEditMode ? 'put' : 'post';
      const response = await axiosInstance[method](endpoint, payload);

      if (response.data && response.data.status) {
        showSuccessToast(isEditMode ? "Batch updated successfully" : "Batch created successfully");
        // If creating new batch, update batchId for future operations
        if (!isEditMode && response.data.data?.batchId) {
          setFormData({ ...formData, batchId: response.data.data.batchId });
          // Navigate to update page only on successful creation
          navigate(`/batches/updateBatch/${response.data.data.batchId}`);
        }
      } else {
        // Show error and stay on the same page
        const errorMessage = response.data?.message || "Failed to save batch";
        showErrorToast(errorMessage);
        console.error("Batch save failed:", response.data);
      }
    } catch (error) {
      console.error("Error saving batch:", error);
      
      // Extract error message from response - handle validation errors
      let errorMessage = "Failed to save batch. Please check your input and try again.";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle validation errors (400 Bad Request)
        if (errorData.errors) {
          const validationErrors = [];
          Object.keys(errorData.errors).forEach(field => {
            const fieldErrors = errorData.errors[field];
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach(err => validationErrors.push(`${field}: ${err}`));
            } else {
              validationErrors.push(`${field}: ${fieldErrors}`);
            }
          });
          errorMessage = validationErrors.join('\n');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.title) {
          errorMessage = errorData.title;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showErrorToast(errorMessage);
      // Stay on the same page - don't navigate on error
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return <Preloader />;
  }

  const pageLabel = isEditMode ? "Update Batch" : "Add Batch";

  return (
    <div className="container-fluid p-3">
      {formLoading && <Preloader />}

      {/* Breadcrumb and Header with Action Buttons */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <Link to="#/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/batches/manageBatch">Manage Batch</Link>
            </li>
            <li className="breadcrumb-item active">{pageLabel}</li>
          </ol>
          <h1 className="mb-0" style={{ fontSize: "1.35rem", fontWeight: "600" }}>
            {pageLabel}
          </h1>
        </div>
        <div className="ms-auto">
          <div className="d-flex justify-content-end align-items-center gap-2">
            {/* Start Batch Button - Only show when status is Draft */}
            {isEditMode && formData.batchStatus === "Draft" && (
              <button
                type="button"
                className="btn"
                onClick={handleStartBatch}
                disabled={formLoading || !formData.batchId}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(40, 167, 69, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.9';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(40, 167, 69, 0.4)';
                }}
              >
                <i className="fas fa-play me-2"></i>
                Start Batch
              </button>
            )}

            {/* Complete Batch Button - Only show when status is InProgress */}
            {isEditMode && formData.batchStatus === "InProgress" && (
              <button
                type="button"
                className="btn"
                onClick={handleOpenCompleteModal}
                disabled={formLoading}
                style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(23, 162, 184, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.9';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(23, 162, 184, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(23, 162, 184, 0.4)';
                }}
              >
                <i className="fas fa-check-circle me-2"></i>
                Complete Batch
              </button>
            )}

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/batches/manageBatch")}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              onClick={handleSubmit}
              disabled={formLoading}
              style={{
                backgroundColor: themeColor,
                color: 'white',
                padding: '8px 20px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: '600',
                fontSize: '14px',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.9';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              {formLoading ? "Saving..." : (isEditMode ? "Update Batch" : "Save Batch")}
            </button>
          </div>
        </div>
      </div>

      {/* Product Selection Panel */}
      <Panel className="mb-3">
        <PanelHeader
          noButton
          style={{
            backgroundColor: themeColor,
            color: "white",
            border: "none",
            borderRadius: "10px 10px 0 0",
            padding: "12px 20px",
            fontWeight: "600",
            fontSize: "15px",
          }}
        >
          <i className="fa fa-box me-2"></i>
          Product Information
        </PanelHeader>
        <PanelBody
          style={{
            padding: "16px 20px",
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            borderRadius: "0 0 10px 10px",
            border: `1px solid ${themeColor}20`,
          }}
        >
          <div className="row g-3">
            <div className="col-md-12">
              <label className="form-label" style={{
                color: isDarkMode ? "#ffffff" : "#000000",
                fontWeight: "600",
                marginBottom: "8px",
              }}>
                Product Name *
              </label>
              <div
                onClick={() => {
                  if (!(isEditMode && formData.batchStatus !== "Draft")) setShowProductModal(true);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                  backgroundColor: isEditMode && formData.batchStatus !== "Draft" ? (isDarkMode ? "#1e1e1e" : "#f8f9fa") : (isDarkMode ? "#2d3436" : "#ffffff"),
                  color: selectedProduct ? (isDarkMode ? "#ffffff" : "#212529") : (isDarkMode ? "#6c757d" : "#6c757d"),
                  cursor: isEditMode && formData.batchStatus !== "Draft" ? "not-allowed" : "pointer",
                  minHeight: "38px",
                }}
              >
                <span style={{ flex: 1, fontSize: "14px" }}>
                  {selectedProduct
                    ? (selectedProduct.menuItem_Name ?? selectedProduct.menuItemName ?? `Item #${formData.menuItemId}`)
                    : "Select a product with recipe"}
                </span>
                {selectedProduct && !(isEditMode && formData.batchStatus !== "Draft") && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductChange(null);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: "4px",
                      color: isDarkMode ? "#adb5bd" : "#6c757d",
                      cursor: "pointer",
                      borderRadius: "4px",
                    }}
                    title="Clear selection"
                    aria-label="Clear selection"
                  >
                    <i className="fa fa-times" />
                  </button>
                )}
                {!(isEditMode && formData.batchStatus !== "Draft") && (
                  <i className="fa fa-chevron-down" style={{ fontSize: "12px", color: isDarkMode ? "#adb5bd" : "#6c757d" }} />
                )}
              </div>
              {menuItems.length === 0 && !loading && (
                <small className="text-muted" style={{
                  display: "block",
                  marginTop: "8px",
                  color: isDarkMode ? "#adb5bd" : "#6c757d",
                }}>
                  No products with recipes found. Please create a recipe first.
                </small>
              )}
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Ingredients Display Panel */}
      {formData.menuItemId > 0 && (
        <Panel className="mb-3">
          <PanelHeader
            noButton
            style={{
              backgroundColor: themeColor,
              color: "white",
              border: "none",
              borderRadius: "10px 10px 0 0",
              padding: "12px 20px",
              fontWeight: "600",
              fontSize: "15px",
            }}
          >
            <i className="fa fa-list-ul me-2"></i>
            {formData.isCompleted ? "Batch Ingredients (Actual Costs)" : "Recipe Ingredients"}
            <span
              className="badge ms-2"
              style={{
                backgroundColor: "rgba(255,255,255,0.25)",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              Unit Cost
            </span>
          </PanelHeader>
          <PanelBody
            style={{
              padding: "16px 20px",
              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
              borderRadius: "0 0 10px 10px",
              border: `1px solid ${themeColor}20`,
            }}
          >
            {loadingIngredients ? (
              <div className="text-center py-4">
                <Preloader />
              </div>
            ) : ingredients.length > 0 ? (
              <div className="table-responsive">
                <table 
                  className="table table-hover mb-0"
                  style={{
                    backgroundColor: 'transparent',
                  }}
                >
                  <thead>
                    <tr style={{
                      backgroundColor: isDarkMode ? '#1e1e1e' : '#f8f9fa',
                      borderBottom: `2px solid ${themeColor}`,
                    }}>
                      <th style={{
                        color: isDarkMode ? '#ffffff' : '#000000',
                        padding: '8px 12px',
                        fontWeight: '600',
                        fontSize: '13px',
                        borderBottom: 'none'
                      }}>
                        Ingredient Name
                      </th>
                      <th style={{
                        color: isDarkMode ? '#ffffff' : '#000000',
                        padding: '8px 12px',
                        fontWeight: '600',
                        fontSize: '13px',
                        textAlign: 'right',
                        borderBottom: 'none'
                      }}>
                        QTY
                      </th>
                      <th style={{
                        color: isDarkMode ? '#ffffff' : '#000000',
                        padding: '8px 12px',
                        fontWeight: '600',
                        fontSize: '13px',
                        borderBottom: 'none'
                      }}>
                        Unit
                      </th>
                      <th style={{
                        color: isDarkMode ? '#ffffff' : '#000000',
                        padding: '8px 12px',
                        fontWeight: '600',
                        fontSize: '13px',
                        textAlign: 'right',
                        borderBottom: 'none'
                      }}>
                        Unit Price
                      </th>
                      <th style={{
                        color: isDarkMode ? '#ffffff' : '#000000',
                        padding: '8px 12px',
                        fontWeight: '600',
                        fontSize: '13px',
                        textAlign: 'right',
                        borderBottom: 'none'
                      }}>
                        Total Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredients.map((ingredient, index) => (
                      <tr 
                        key={ingredient.id}
                        style={{
                          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                          borderBottom: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`,
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isDarkMode ? '#353b45' : '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isDarkMode ? '#2d3436' : '#ffffff';
                        }}
                      >
                        <td style={{
                          color: isDarkMode ? '#ffffff' : '#212529',
                          padding: '8px 12px',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}>
                          {ingredient.ingredientName}
                        </td>
                        <td style={{
                          color: isDarkMode ? '#ffffff' : '#212529',
                          padding: '8px 12px',
                          fontSize: '13px',
                          textAlign: 'right',
                          fontWeight: '500'
                        }}>
                          {parseFloat(ingredient.quantity).toLocaleString('en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                          })}
                        </td>
                        <td style={{
                          color: isDarkMode ? '#adb5bd' : '#6c757d',
                          padding: '8px 12px',
                          fontSize: '13px',
                          fontWeight: '400'
                        }}>
                          {ingredient.unit}
                        </td>
                        <td style={{
                          color: isDarkMode ? '#ffffff' : '#212529',
                          padding: '8px 12px',
                          fontSize: '13px',
                          textAlign: 'right',
                          fontWeight: '500'
                        }}>
                          ${parseFloat(ingredient.costPrice || 0).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </td>
                        <td style={{
                          color: isDarkMode ? '#ffffff' : '#212529',
                          padding: '8px 12px',
                          fontSize: '13px',
                          textAlign: 'right',
                          fontWeight: '600'
                        }}>
                          ${parseFloat(ingredient.cost || 0).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{
                      backgroundColor: isDarkMode ? '#1e1e1e' : '#f8f9fa',
                      borderTop: `2px solid ${themeColor}`,
                    }}>
                      <td colSpan="4" style={{
                        color: isDarkMode ? '#ffffff' : '#000000',
                        padding: '8px 12px',
                        fontWeight: '600',
                        fontSize: '13px',
                        textAlign: 'right'
                      }}>
                        {formData.isCompleted ? "Total Actual Cost:" : "Total Estimated Cost:"}
                      </td>
                      <td style={{
                        color: themeColor,
                        padding: '8px 12px',
                        fontWeight: '700',
                        fontSize: '14px',
                        textAlign: 'right'
                      }}>
                        ${ingredients.reduce((sum, ing) => sum + (parseFloat(ing.cost) || 0), 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-4" style={{
                color: isDarkMode ? '#adb5bd' : '#6c757d'
              }}>
                <i className="fas fa-info-circle fa-2x mb-3" style={{ opacity: 0.5 }}></i>
                <p className="mb-0">No ingredients found for this product.</p>
                <small>Please add ingredients to the recipe first.</small>
              </div>
            )}
          </PanelBody>
        </Panel>
      )}

      {/* Batch Details Panel */}
      <Panel className="mb-3">
        <PanelHeader
          noButton
          style={{
            backgroundColor: themeColor,
            color: "white",
            border: "none",
            borderRadius: "10px 10px 0 0",
            padding: "12px 20px",
            fontWeight: "600",
            fontSize: "15px",
          }}
        >
          <i className="fa fa-clipboard-list me-2"></i>
          {pageLabel}
        </PanelHeader>
        <PanelBody
          style={{
            padding: "16px 20px",
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            borderRadius: "0 0 10px 10px",
            border: `1px solid ${themeColor}20`,
          }}
        >
          <div className="row g-3">
            <div className="col-md-6">
              <FormInput
                label="Batch Number *"
                placeHolder="Enter Batch Number"
                type="text"
                value={formData.batchNumber}
                onchange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                required
                disabled={isEditMode && formData.batchStatus !== "Draft"}
              />
            </div>
            <div className="col-md-4">
              <FormInput
                label="Cost Price *"
                placeHolder="Enter Cost Price"
                type="number"
                step="0.01"
                value={formData.costPrice}
                onchange={(e) => setFormData({ ...formData, costPrice: e.target.value })}
                required
              />
            </div>
            <div className="col-md-4 d-none">
              <FormInput
                label="Expiry Date"
                placeHolder="Select Expiry Date"
                type="date"
                value={formData.expiryDate}
                onchange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
              />
            </div>
            {/* Show Batch Status as read-only display when in edit mode */}
            {isEditMode && (
              <div className="col-md-4">
                <label className="form-label" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                  Batch Status
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.batchStatus}
                  readOnly
                  style={{
                    backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
                    border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                    color: isDarkMode ? "#ffffff" : "#000000",
                    cursor: 'not-allowed',
                  }}
                />
              </div>
            )}
            
            {/* Show Quantity and Unit as read-only when batch is completed */}
            {isEditMode && formData.batchStatus === "Completed" && (
              <>
                <div className="col-md-6">
                  <label className="form-label" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                    Quantity
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.quantity || "N/A"}
                    readOnly
                    style={{
                      backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
                      border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                      color: isDarkMode ? "#ffffff" : "#000000",
                      cursor: 'not-allowed',
                    }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                    Unit
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.unit || "N/A"}
                    readOnly
                    style={{
                      backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
                      border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                      color: isDarkMode ? "#ffffff" : "#000000",
                      cursor: 'not-allowed',
                    }}
                  />
                </div>
                {formData.completedDate && (
                  <div className="col-md-6">
                    <label className="form-label" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                      Completed Date
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={new Date(formData.completedDate).toLocaleDateString()}
                      readOnly
                      style={{
                        backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
                        border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                        color: isDarkMode ? "#ffffff" : "#000000",
                        cursor: 'not-allowed',
                      }}
                    />
                  </div>
                )}
              </>
            )}
            
            <div className="col-md-6 d-none">
              <FormInput
                label="Total Production Cost"
                placeHolder="Enter Total Production Cost"
                type="number"
                step="0.01"
                value={formData.totalProductionCost}
                onchange={(e) => setFormData({ ...formData, totalProductionCost: e.target.value })}
              />
            </div>
            <div className="col-md-6 d-none">
              <FormInput
                label="Cost Per Unit"
                placeHolder="Enter Cost Per Unit"
                type="number"
                step="0.01"
                value={formData.costPerUnit}
                onchange={(e) => setFormData({ ...formData, costPerUnit: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <FormInput
                label="Waste Quantity"
                placeHolder="Enter Waste Quantity"
                type="number"
                step="0.01"
                value={formData.wasteQuantity}
                onchange={(e) => setFormData({ ...formData, wasteQuantity: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <FormInput
                label="Waste Reason"
                placeHolder="Enter Waste Reason"
                type="text"
                value={formData.wasteReason}
                onchange={(e) => setFormData({ ...formData, wasteReason: e.target.value })}
              />
            </div>
            <div className="col-md-12">
              <FormInput
                label="Notes"
                placeHolder="Enter Notes"
                type="textarea"
                value={formData.notes}
                onchange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>

        </PanelBody>
      </Panel>

      {/* Product Selection Modal */}
      <ProductWithRecipeSelectionModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        onSelectProduct={(option) => handleProductChange(option)}
        compID={CompID}
      />

      {/* Complete Batch Modal */}
      <CustomModal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        onSubmit={handleCompleteBatch}
        title="Complete Batch"
        showButton={true}
        isCentered={true}
        submitButtonText="Complete"
        submitButtonDisabled={formLoading}
        modalSize="md"
      >
        {formLoading && <Preloader />}
        <div className="row g-3">
          <div className="col-md-12">
            <FormInput
              label="Batch Number *"
              placeHolder="Enter Batch Number"
              type="text"
              value={completeFormData.batchNumber}
              onchange={(e) => setCompleteFormData({ ...completeFormData, batchNumber: e.target.value })}
              required
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Final Quantity *"
              placeHolder="Enter Final Quantity"
              type="number"
              step="0.01"
              value={completeFormData.finalQuantity}
              onchange={(e) => setCompleteFormData({ ...completeFormData, finalQuantity: e.target.value })}
              required
            />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
              Final Unit *
            </label>
            {filteredUnits.length > 0 ? (
              <Select
                options={filteredUnits.map(unit => ({
                  value: unit.measureName || unit.MeasureName,
                  label: unit.measureName || unit.MeasureName,
                }))}
                value={filteredUnits.find(unit => 
                  (unit.measureName || unit.MeasureName) === completeFormData.finalUnit
                ) ? { value: completeFormData.finalUnit, label: completeFormData.finalUnit } : null}
                onChange={(selected) => setCompleteFormData({ ...completeFormData, finalUnit: selected?.value || "" })}
                styles={dropdownStyles}
                placeholder="Select Unit"
                isClearable
              />
            ) : (
              <input
                type="text"
                className="form-control"
                placeholder="Enter Unit"
                value={completeFormData.finalUnit}
                onChange={(e) => setCompleteFormData({ ...completeFormData, finalUnit: e.target.value })}
                style={{
                  backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                  border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              />
            )}
          </div>
          <div className="col-md-12">
            <FormInput
              label="Expiry Date"
              placeHolder="Select Expiry Date"
              type="datetime-local"
              value={completeFormData.expiryDate}
              onchange={(e) => setCompleteFormData({ ...completeFormData, expiryDate: e.target.value })}
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default CreateUpdateBatch;
